import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { PawPrint, Plus, Trash2, Calendar, Phone, Stethoscope, Wallet } from "lucide-react";
import ImageUploader, { imageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

const SPECIES = ["Dog", "Cat", "Bird", "Fish", "Reptile", "Small mammal", "Other"];
const EXPENSE_TYPES = ["Vet visit", "Vaccination", "Food", "Grooming", "Medicine", "Boarding", "Other"];

export default function Pets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = {
    name: "", species: "Dog", breed: "", birth_date: "", feeding_notes: "",
    vet_name: "", vet_phone: "", next_vaccination: "", last_vaccination: "", notes: "",
    image_path: null,
  };
  const [form, setForm] = useState(blank);
  const [newId, setNewId] = useState(null);
  const [expenseFor, setExpenseFor] = useState(null);
  const [expenses, setExpenses] = useState({});
  const expBlank = { date: new Date().toISOString().slice(0, 10), type: "Vet visit", cost: "", provider: "", notes: "" };
  const [expForm, setExpForm] = useState(expBlank);

  const load = useCallback(async () => { const { data } = await api.get("/pets"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const loadExpenses = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/pets/${pid}/expenses`);
      setExpenses((m) => ({ ...m, [pid]: data }));
    } catch (e) { console.error(e); }
  }, []);

  const openExpense = (pet) => {
    setExpenseFor(pet); setExpForm(expBlank);
    if (!expenses[pet.id]) loadExpenses(pet.id);
  };

  const saveExpense = async () => {
    if (!expForm.date || !expForm.type) return toast.error("Date and type required");
    try {
      const payload = { ...expForm, cost: expForm.cost ? parseFloat(expForm.cost) : null };
      await api.post(`/pets/${expenseFor.id}/expenses`, payload);
      toast.success("Expense logged");
      setExpForm(expBlank);
      loadExpenses(expenseFor.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeExpense = async (pid, lid) => {
    try {
      await api.delete(`/pets/${pid}/expenses/${lid}`);
      loadExpenses(pid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Pet name required");
    try {
      if (editId) {
        await api.put(`/pets/${editId}`, form);
        toast.success("Saved"); setOpen(false); setEditId(null); setForm(blank); load();
      } else {
        const { data } = await api.post("/pets", form);
        setNewId(data.id); // allow image upload from form
        toast.success("Pet added — you can add a photo now", { duration: 1800 });
        load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (it) => { setForm({ ...blank, ...it }); setEditId(it.id); setNewId(it.id); setOpen(true); };
  const remove = async (id) => { await api.delete(`/pets/${id}`); load(); };

  const ageYears = (b) => {
    if (!b) return null;
    const d = new Date(b);
    const ms = Date.now() - d.getTime();
    return Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  };

  return (
    <div>
      <PageHeader
        eyebrow="Family members"
        title="Pets"
        subtitle="Vaccinations, vet contacts, feeding rituals — for the four-legged ones too."
        action={
          <Button data-testid="pet-add-button" onClick={() => { setEditId(null); setNewId(null); setForm(blank); setOpen(true); }}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
            <Plus size={15} className="mr-1.5" /> Add pet
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={PawPrint} title="No pets yet." message="Add furry, feathered or finned family members so vaccination dates and vet contacts stay handy." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} data-testid={`pet-card-${p.id}`} className="warm-card overflow-hidden fade-up">
              <div className="h-44 bg-hearth-stone relative overflow-hidden">
                {p.image_path ? (
                  <img src={imageUrl(p.image_path)} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center">
                    <PawPrint size={42} className="text-hearth-clay/30" />
                  </div>
                )}
                <span className="absolute top-3 left-3 chip bg-hearth-paper/90 text-hearth-ink backdrop-blur">{p.species}</span>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-2xl text-hearth-ink leading-tight">{p.name}</h3>
                    <p className="caption mt-0.5">{p.breed || p.species}{ageYears(p.birth_date) != null ? ` · ${ageYears(p.birth_date)} yr` : ""}</p>
                  </div>
                  <button data-testid={`pet-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {p.next_vaccination && (
                    <div className="flex items-center gap-2 text-hearth-ink">
                      <Calendar size={13} className="text-hearth-clay" />
                      <span className="text-hearth-mute">Next shot:</span> <span className="font-mono">{p.next_vaccination}</span>
                    </div>
                  )}
                  {p.vet_name && (
                    <div className="flex items-center gap-2 text-hearth-ink">
                      <Stethoscope size={13} className="text-hearth-sageDark" />
                      <span className="truncate">{p.vet_name}</span>
                    </div>
                  )}
                </div>
                {p.feeding_notes && <p className="text-xs text-hearth-mute mt-3 leading-relaxed line-clamp-2">{p.feeding_notes}</p>}
                <div className="mt-4 flex gap-2 pt-3 border-t border-hearth-line/70">
                  {p.vet_phone && <a href={`tel:${p.vet_phone}`} className="pill-btn bg-hearth-sage/12 text-hearth-sageDark hover:bg-hearth-sage/20 inline-flex items-center gap-1"><Phone size={12} /> Vet</a>}
                  <Button variant="ghost" data-testid={`pet-expense-${p.id}`} onClick={() => openExpense(p)} className="rounded-full text-sm"><Wallet size={13} className="mr-1" /> Expenses</Button>
                  <Button variant="ghost" data-testid={`pet-edit-${p.id}`} onClick={() => startEdit(p)} className="rounded-full text-sm ml-auto">Edit</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setNewId(null); setForm(blank); } }}>
        <DialogContent className="rounded-2xl border-hearth-line bg-hearth-paper max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editId ? "Edit pet" : "Add a pet"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(editId || newId) && (
              <div className="md:col-span-2 h-44">
                <ImageUploader
                  kind="pets" itemId={editId || newId} value={form.image_path}
                  onUploaded={(p) => setForm((f) => ({ ...f, image_path: p }))}
                  className="h-full"
                />
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="caption">Name</Label>
              <Input data-testid="pet-form-name" className="input-warm mt-1.5 h-10" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bruno" />
            </div>
            <div>
              <Label className="caption">Species</Label>
              <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="pet-form-species"><SelectValue /></SelectTrigger>
                <SelectContent>{SPECIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="caption">Breed</Label><Input className="input-warm mt-1.5 h-10" value={form.breed || ""} onChange={(e) => setForm({ ...form, breed: e.target.value })} /></div>
            <div><Label className="caption">Birthday</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
            <div><Label className="caption">Last vaccination</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.last_vaccination || ""} onChange={(e) => setForm({ ...form, last_vaccination: e.target.value })} /></div>
            <div><Label className="caption">Next vaccination</Label><Input type="date" className="input-warm mt-1.5 h-10" value={form.next_vaccination || ""} onChange={(e) => setForm({ ...form, next_vaccination: e.target.value })} /></div>
            <div><Label className="caption">Vet name</Label><Input className="input-warm mt-1.5 h-10" value={form.vet_name || ""} onChange={(e) => setForm({ ...form, vet_name: e.target.value })} /></div>
            <div><Label className="caption">Vet phone</Label><Input className="input-warm mt-1.5 h-10" value={form.vet_phone || ""} onChange={(e) => setForm({ ...form, vet_phone: e.target.value })} placeholder="+61 4XX XXX XXX" /></div>
            <div className="md:col-span-2"><Label className="caption">Feeding notes</Label><Textarea className="input-warm mt-1.5" rows={2} value={form.feeding_notes || ""} onChange={(e) => setForm({ ...form, feeding_notes: e.target.value })} placeholder="2 cups dry food twice a day" /></div>
            <div className="md:col-span-2"><Label className="caption">Notes</Label><Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Close</Button>
            <Button data-testid="pet-form-save" onClick={save} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">{editId ? "Save" : (newId ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!expenseFor} onOpenChange={(v) => !v && setExpenseFor(null)}>
        <DialogContent className="rounded-2xl border-hearth-line bg-hearth-paper max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">{expenseFor?.name} — expenses</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="col-span-2 md:col-span-1"><Label className="caption">Date</Label><Input data-testid="pet-exp-date" type="date" className="input-warm mt-1.5 h-10" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} /></div>
            <div className="col-span-2 md:col-span-1">
              <Label className="caption">Type</Label>
              <Select value={expForm.type} onValueChange={(v) => setExpForm({ ...expForm, type: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-10 rounded-xl" data-testid="pet-exp-type"><SelectValue/></SelectTrigger>
                <SelectContent>{EXPENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="caption">Cost</Label><Input data-testid="pet-exp-cost" type="number" step="0.01" className="input-warm mt-1.5 h-10" value={expForm.cost} onChange={(e) => setExpForm({ ...expForm, cost: e.target.value })} /></div>
            <div className="col-span-2"><Label className="caption">Provider</Label><Input className="input-warm mt-1.5 h-10" value={expForm.provider} onChange={(e) => setExpForm({ ...expForm, provider: e.target.value })} /></div>
            <div className="col-span-2 md:col-span-5"><Button data-testid="pet-exp-save" onClick={saveExpense} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"><Plus size={13} className="mr-1"/>Add expense</Button></div>
          </div>
          <div className="mt-2 divide-y divide-hearth-line/70">
            {(expenses[expenseFor?.id] || []).length === 0 ? (
              <p className="text-sm text-hearth-mute italic py-3">No expenses logged yet.</p>
            ) : (expenses[expenseFor?.id] || []).map((e) => (
              <div key={e.id} data-testid={`pet-exp-row-${e.id}`} className="py-3 flex items-center gap-3 text-sm">
                <div className="font-mono text-xs text-hearth-mute w-24">{fmtDate(e.date)}</div>
                <div className="font-medium text-hearth-ink flex-1 truncate">{e.type}{e.provider ? ` · ${e.provider}` : ""}</div>
                <div className="font-mono text-sm text-hearth-ink w-20 text-right">{money(e.cost)}</div>
                <button onClick={() => removeExpense(expenseFor.id, e.id)} className="p-1.5 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
